import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material'
import { useQuery } from 'react-query'
import { TreeViewCheck } from 'src/components/uiElements/styledComponents/TreeViewCheck'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { TextInput, useListContext, useTranslate, DateInput, DateTimeInput, BooleanInput } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { SplitByCharacterWithPosition } from 'src/helper/splitByCharacter'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { countryServices } from 'src/services/reservation/country/country.service'
import { userServices } from 'src/services/reservation/user/user.service'
import { SelectInput } from '../fields/SelectInput'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { productServices } from 'src/services/reservation/product/product.service'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { promotionServices } from 'src/services/reservation/promotion/promotion.service'
import { Close, HighlightOff, Search } from '@mui/icons-material'
import { tagServices } from 'src/services/reservation/tag/tag.service'
import { sourceServices } from 'src/services/reservation/source/source.service'
import get from 'lodash/get'
import { AutocompleteInput } from '../fields/AutocompleteInput'
import { MultiAutocompleteInput } from 'src/components/uiElements/fields/MultiAutocompleteInput';
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { basesServices } from 'src/services/reservation/bases/Bases.service'
import { toISODate } from 'src/utils/dateUtils'

export const PostFilterButtonReservation = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const assignIdToValue = (objects, labelField) => {
  if (Array.isArray(objects)) {
    return objects.map((obj) => {
      const newObj = {
        id: obj?.id,
        fiscal_data_relation: obj?.fiscal_data_relation,
        label: obj?.label,
        token: obj?.token,
        fantasy_name: obj?.fantasy_name,
        short_name: obj?.short_name,
        long_name: obj?.long_name,
        value: obj?.value,
        children: obj?.children || null,
      }

      // Usar 'get' para acceder a propiedades anidadas
      newObj.label = labelField
        ?.map((field) => get(obj, field))
        .filter((elem) => elem)
        .join(' - ')
      if (newObj.id !== undefined) {
        newObj.value = String(newObj.id) + '__' + newObj.label
      }

      if (newObj.children && newObj.children.length > 0) {
        newObj.children = assignIdToValue(newObj.children, labelField)
      } else {
        newObj.children = null
      }

      return newObj
    })
  }
  return null
}

const getDefaultValueTreeView = (data, value) => {
  let valueAux = []
  data.forEach((elem) => {
    if (elem.id === value) {
      valueAux.push(elem.value)
    } else if (elem.children && elem.children.length > 0) {
      valueAux = valueAux.concat(getDefaultValueTreeView(elem.children, value))
    }
  })

  return valueAux
}

const filterTreeView = (data, value, labelField) => {
  let expanded = []

  if (value) {
    const dataAux = []
    data.forEach((elem) => {
      const [filteredChildren, expandedChildren] = elem.children
        ? filterTreeView(elem.children, value, labelField)
        : [[], []]
      if (filteredChildren.length > 0) {
        elem.children = filteredChildren
        dataAux.push(elem)
        expanded = expanded.concat([elem.value, expandedChildren]).flat()
      } else if (elem[labelField]?.toLowerCase()?.includes(value)) {
        dataAux.push(elem)
      }
    })

    return [dataAux, expanded]
  }

  return [data, expanded]
}

const getId = (value) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 0)).join(',')
  }
}

const getLabel = (value, emptyText) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 1)).join(', ')
  }

  return emptyText
}

const SelectArrayInputFn = ({
  queryName,
  label,
  optionsText = ['long_name'],
  optionValue,
  source,
  queryFn,
  ...props
}) => {
  const options = useQuery(queryName, () => queryFn())
  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={options?.data || []}
        optionsText={optionsText}
        optionValue={optionValue}
        multiple
        showSearch
        {...props}
      />
    </FormControl>
  )
}

const PromotionFilterInput = ({ source, label }) => {
  const translate = useTranslate()
  const options = useQuery('promotion-all', () => promotionServices.getByAll({ limit: 999 }))
  
  const formatOptions = () => {
    if (!options.data) return []
    const allOption = { id: 'ALL', name: translate('ra.action.all_promotions') }
    const promotionOptions = options.data.map(promo => ({
      id: promo.coupons?.map(c => c.code).join(',') || promo.id,
      name: promo.name
    }))
    return [allOption, ...promotionOptions]
  }

  const parseValue = (value) => {
    if (!value || value.length === 0) return undefined
    if (value.includes('ALL')) return 'ALL'
    return value.join(',')
  }

  const formatValue = (value) => {
    if (!value) return []
    if (value === 'ALL') return ['ALL']
    return value.split(',')
  }

  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={formatOptions()}
        optionsText={['name']}
        optionValue='id'
        multiple
        showSearch
        format={formatValue}
        parse={parseValue}
      />
    </FormControl>
  )
}

export const TreeViewInput = ({
  queryName,
  fieldName,
  modalTitle,
  buttonText,
  labelField,
  queryFn,
  limitOptions = 30,
}) => {
  const options = useQuery(queryName, () => queryFn())
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState([])
  const [currentOption, setCurrentOption] = useState([])
  const [formatedOptions, setFormatedOptions] = useState([])
  const { setValue } = useFormContext()
  const { filterValues } = useListContext()

  useEffect(() => {
    if (open) {
      setFormatedOptions(assignIdToValue(options.data.slice(0, limitOptions), labelField))
      setExpanded([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!options.isLoading) {
      const nodes = assignIdToValue(options.data.slice(0, limitOptions), labelField)
      const defaultValues = []
      filterValues[fieldName]?.split(',')?.forEach((elem) => {
        const id = parseInt(elem) || null
        if (id) {
          const defaultValue = getDefaultValueTreeView(nodes, id)
          defaultValues.push(defaultValue)
        }
      })
      setCurrentOption(defaultValues.flat())
      setFormatedOptions(nodes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isLoading])

  useEffect(() => {
    if (currentOption) {
      setValue(fieldName, getId(currentOption))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption])

  const onSearch = (value) => {
    if (value.length >= 3) {
      const nodes = assignIdToValue(options.data, labelField)
      const [filteredNodes, expandedNodes] = filterTreeView(nodes, value.toLowerCase(), 'label')
      setFormatedOptions(filteredNodes)
      setExpanded(expandedNodes)
    } else {
      // Si la búsqueda es menos de 3 caracteres, muestra solo las opciones seleccionadas o resetea a la vista inicial
      setFormatedOptions(
        currentOption.length
          ? currentOption
          : assignIdToValue(options.data.slice(0, limitOptions), labelField)
      )
    }
  }

  const onCloseModal = () => {
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} maxWidth='md' fullWidth onClose={onCloseModal}>
        <DialogTitle display='flex' justifyContent='space-between'>
          {modalTitle}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size='small'
            placeholder={translate('ra.message.select_at_least', { num: 3 })}
            style={{ marginBottom: 24 }}
            onChange={(e) => e.target.value.length >= 3 && onSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {formatedOptions.length >= limitOptions && (
            <Typography variant='caption' display='block' style={{ color: 'gray', marginLeft: 10 }}>
              vista previa de resultados ({limitOptions}).
            </Typography>
          )}
          <TreeViewCheck
            checked={currentOption}
            setChecked={setCurrentOption}
            nodes={formatedOptions}
            setExpanded={setExpanded}
            expanded={expanded}
            onlyOne={false}
            noCascade={false}
            activeHistorical={true}
            allNodes={assignIdToValue(options.data, labelField)}
          />
        </DialogContent>
      </Dialog>

      <Box width={'100%'}>
        {options.isLoading ? (
          <Skeleton variant='rectangular' width={'250px'} height={'40px'} />
        ) : (
          <Button
            sx={{
              width: '100%',
              height: '40px',
            }}
            onClick={() => setOpen(true)}
            variant='outlined'
          >
            <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
              {getLabel(currentOption, buttonText)}
            </span>
          </Button>
        )}
      </Box>
    </>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      if (values.business_unit__country_id__in) {
        values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
      }
      if(prop === 'user_id__in'){
        localStorage.removeItem('seller')
      }
      if(prop === 'business_unit__salesperson_user_id__in'){
        localStorage.removeItem('commercial_responsible')
      }
      if(prop === 'business_unit__user_id__in'){
        localStorage.removeItem('manager_responsible')
      }
      setFilters(values, copy, false)
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}

const CountryInput = ({ label, source }) => {
  const [loadingCountryList, setLoadingCountryList] = useState(false)
  const [countryList, setCountryList] = useState([])

  const getCountry = async (long_name) => {
    try {
      setLoadingCountryList(true)
      const allCountries = await countryServices.getByAll({ long_name: long_name })
      setCountryList(allCountries)
      setLoadingCountryList(false)
    } catch (error) {
      setCountryList([])
      setLoadingCountryList(false)
    }
  }

  return (
    <AutocompleteInput
      source={source}
      label={label}
      options={countryList}
      loading={loadingCountryList}
      optionsText={['long_name']}
      optionValue='id'
      onSearch={getCountry}
      resetOnLoad={false}
      style={{ width: '100%' }}
      positionOptionsText={0}
      multiple
    />
  )
}


export const PostFilterFormReservation = () => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const [usersList, setUserList] = useState([])
  const [usersLoading, setLoadingUserList] = useState(false)
  const [openSellerDialog, setOpenSellerDialog] = useState(false)
  const [openCommercialResponsibleDialog, setOpenCommercialResponsibleDialog] = useState(false)
  const [openManagerResponsibleDialog, setOpenManagerResponsibleDialog] = useState(false)
  const form = useForm()
  const usersForm = useForm()

  useEffect(() => {
    Object.entries(filterValues).forEach((entry) => {
      const [key, value] = entry
      form.setValue(key, value)
    })
    if(localStorage.getItem('seller')){
      usersForm.setValue('seller', JSON.parse(localStorage.getItem('seller')))
    } else {
      usersForm.setValue('seller',[])
    }
    if(localStorage.getItem('manager_responsible')){
      usersForm.setValue('manager_responsible', JSON.parse(localStorage.getItem('manager_responsible')))
    } else {
      usersForm.setValue('manager_responsible',[])
    }
    if(localStorage.getItem('commercial_responsible')){
      usersForm.setValue('commercial_responsible', JSON.parse(localStorage.getItem('commercial_responsible')))
    } else {
      usersForm.setValue('commercial_responsible',[])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(()=>{
    if(!form.getValues('user_id__in')){
      usersForm.setValue('seller',[])
    }
  },[form.watch('user_id__in')])

  useEffect(()=>{
    if(!form.getValues('business_unit__salesperson_user_id__in')){
      usersForm.setValue('commercial_responsible',[])
    }
  },[form.watch('business_unit__salesperson_user_id__in')])

  useEffect(()=>{
    if(!form.getValues('business_unit__user_id__in')){
      usersForm.setValue('manager_responsible',[])
    }
  },[form.watch('business_unit__user_id__in')])

  useEffect(()=>{
    if (form.getValues('billing__situation__in') === 'BILLED') {
      form.setValue('billing_situation', true)
    }
  },[form.watch('billing__situation__in')])

  let searchTimeout
  const getUserB2 = async (user, length = 2) => {
    clearTimeout(searchTimeout)

    if (
      (!user?.firstname || user.firstname.length < length) &&
      (!user?.surname || user.surname.length < length)
    ) {
      setUserList([])
      return
    }

    searchTimeout = setTimeout(async () => {
      try {
        setLoadingUserList(true)
        let filter = {
          limit: 20
        }
        if (user?.firstname) {
          filter.firstname = user?.firstname
        }
        if (user?.surname) {
          filter.surname = user?.surname
        }
        const allUser = await reservationServices.userReservtionFilter(filter)
        setUserList(allUser)
      } catch (error) {
        setUserList([])
      } finally {
        setLoadingUserList(false)
      }
    }, 1000)
  }

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      if (values.business_unit__country_id__in) {
        values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
      }
      if(values.item__product_id__in && Array.isArray(values.item__product_id__in)){
        for(let prod of values.item__product_id__in){
          if(typeof prod !== 'number' || Number.isNaN(prod)){
            values.item__product_id__in = null
            break
          }
        }
      }
      if(values.tag__id__in && Array.isArray(values.tag__id__in)){
        for(let tag of values.tag__id__in){
          if(typeof tag !== 'number' || Number.isNaN(tag)){
            values.tag__id__in = null
            break
          }
        }
      }
      delete values.billing__situation__in
      delete values.billing__situation__not_in
      if (displayedFilters?.billing_situation) {
        if (values.billing_situation) {
          values['billing__situation__in'] = 'BILLED'
        } else {
          values['billing__situation__not_in'] = 'BILLED'
        }
      }
      delete values.billing_situation
      setFilters(values, displayedFilters, false)
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }

  function convertToUTC(dateString) {
    return dateString
  }

  function convertFromUTC(dateString) {
    return dateString
  }

  return (
    <>
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.create_at && (
              <RemoveFilterField prop={'create_at'} fields={['create_at__gte', 'create_at__lte']} width={500}>
                <DateTimeInput
                  label={translate('ra.field.start_create_date')}
                  fullWidth
                  source='create_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_create_date')}
                  fullWidth
                  source='create_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.number__ilike_in && (
              <RemoveFilterField prop={'number__ilike_in'} fields={['number__ilike_in']} width={300}>
                <TextInput
                  label={translate('ra.field.reservation_number')}
                  fullWidth
                  source='number__ilike_in'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.work_unit_id__in && (
              <RemoveFilterField
                prop={'work_unit_id__in'}
                fields={['work_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  queryName='work-unit-all'
                  fieldName='work_unit_id__in'
                  modalTitle={translate('ra.title.select_work_unit')}
                  buttonText={translate('ra.message.filter_by_work_unit')}
                  labelField={['label']}
                  queryFn={workUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit_id__in && (
              <RemoveFilterField
                prop={'business_unit_id__in'}
                fields={['business_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  queryName='business-unit-all'
                  fieldName='business_unit_id__in'
                  modalTitle='Selecciona una unidad de negocio'
                  buttonText={translate('ra.message.filter_by_business_unit')}
                  labelField={['fiscal_data_relation.name', 'fantasy_name']}
                  queryFn={businessUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__country_id__in && (
              <RemoveFilterField
                prop={'business_unit__country_id__in'}
                fields={['business_unit__country_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <CountryInput label={translate('ra.field.country')} source='business_unit__country_id__in' />
              </RemoveFilterField>
            )}
            {displayedFilters?.user_id__in && (
              <RemoveFilterField prop={'user_id__in'} fields={['user_id__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenSellerDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {
                          (form.getValues('user_id__in') && form.getValues('user_id__in').length > 0) ?
                          usersForm.getValues('seller').map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.message.filter_by_seller')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__salesperson_user_id__in && (
              <RemoveFilterField prop={'business_unit__salesperson_user_id__in'} fields={['business_unit__salesperson_user_id__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenCommercialResponsibleDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {

                          (form.getValues('business_unit__salesperson_user_id__in') && form.getValues('business_unit__salesperson_user_id__in').length > 0) ?
                          usersForm.getValues('commercial_responsible').map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.field.commercial_responsible')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__user_id__in && (
              <RemoveFilterField prop={'business_unit__user_id__in'} fields={['business_unit__user_id__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenManagerResponsibleDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {
                          (form.getValues('business_unit__user_id__in') && form.getValues('business_unit__user_id__in').length > 0) ?
                          usersForm.getValues('manager_responsible').map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.field.manager_responsible')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.business_unit__user_id__in && (
              <RemoveFilterField
                prop={'business_unit__user_id__in'}
                fields={['business_unit__user_id__in']}
                width={300}
              >
                <SelectArrayInputFn
                  source='business_unit__user_id__in'
                  queryName='users-all'
                  label={translate('ra.field.manager_responsible')}
                  queryFn={userServices.getByAll}
                  optionsText={['firstname', 'surname']}
                  separator=' '
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__salesperson_user_id__in && (
              <RemoveFilterField
                prop={'business_unit__salesperson_user_id__in'}
                fields={['business_unit__salesperson_user_id__in']}
                width={300}
              >
                <SelectArrayInputFn
                  source='business_unit__salesperson_user_id__in'
                  queryName='users-all'
                  label={translate('ra.field.commercial_responsible')}
                  queryFn={userServices.getByAll}
                  optionsText={['firstname', 'surname']}
                  separator=' '
                />
              </RemoveFilterField>
            )} */}
            {displayedFilters?.status__in && (
              <RemoveFilterField prop={'status__in'} fields={['status__in']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.reservation_status')}</InputLabel>
                  <SelectInput
                    source='status__in'
                    options={[
                      { id: 'OPEN', name: translate('ra.status.OPEN') },
                      { id: 'FINALIZED', name: translate('ra.status.FINALIZED') },
                      { id: 'CLOSED', name: translate('ra.status.CLOSED') },
                      { id: 'CANCELLED', name: translate('ra.status.CANCELLED') },
                      { id: 'DELETED', name: translate('ra.status.DELETED') }
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.date_in && (
              <RemoveFilterField prop={'date_in'} fields={['date_in__gte', 'date_in__lte']} width={500}>
                <DateInput
                  label={translate('ra.field.start_date_first_service')}
                  fullWidth
                  source='date_in__gte'
                />
                <DateInput
                  label={translate('ra.field.end_date_first_service')}
                  fullWidth
                  source='date_in__lte'
                  sx={{ ml: 5 }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.pax && (
              <RemoveFilterField
                prop={'pax'}
                fields={['pax__name__ilike', 'pax__surname__ilike']}
                width={500}
              >
                <TextInput label={translate('ra.field.name')} fullWidth source='pax__name__ilike' />
                <TextInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.surname')}
                  fullWidth
                  source='pax__surname__ilike'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.product_type_id__in && (
              <RemoveFilterField prop={'product_type_id__in'} fields={['product_type_id__in']} width={300}>
                <SelectArrayInputFn
                  source='product_type_id__in'
                  queryName='product-type-all'
                  label={translate('ra.field.product_type')}
                  queryFn={() => productTypeServices.getByParamByUser()}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__product_id__in && (
              <RemoveFilterField prop={'item__product_id__in'} fields={['item__product_id__in']} width={300}>
                <SelectArrayInputFn
                  source='item__product_id__in'
                  queryName='product-all'
                  label={translate('ra.field.product')}
                  queryFn={productServices.getByAll}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__source_id__in && (
              <RemoveFilterField prop={'item__source_id__in'} fields={['item__source_id__in']} width={300}>
                <SelectArrayInputFn
                  source='item__source_id__in'
                  queryName='source-all'
                  label={translate('ra.field.source')}
                  queryFn={sourceServices.getByAll}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__due_date_final && (
              <RemoveFilterField
                prop={'item__due_date_final'}
                fields={['item__due_date_final__gte', 'item__due_date_final__lte']}
                width={500}
              >
                <DateInput
                  label={translate('ra.field.start_due_date')}
                  fullWidth
                  source='item__due_date_final__gte'
                />
                <DateInput
                  label={translate('ra.field.end_due_date')}
                  fullWidth
                  source='item__due_date_final__lte'
                  sx={{ ml: 5 }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__status__in && (
              <RemoveFilterField prop={'item__status__in'} fields={['item__status__in']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.item_status')}</InputLabel>
                  <SelectInput
                    source='item__status__in'
                    options={[
                      { id: 'CONFIRMED', name: translate('ra.status.CONFIRMED') },
                      { id: 'REJECTED', name: translate('ra.status.REJECTED_MALE') },
                      { id: 'CANCELLED', name: translate('ra.status.CANCELLED') },
                      { id: 'CANCELLED_COST', name: translate('ra.status.CANCELLED_COST') },
                      { id: 'PENDING', name: translate('ra.status.PENDING') },
                      { id: 'INVOICED', name: translate('ra.status.INVOICED') },
                      { id: 'REFUNDED', name: translate('ra.status.REFUNDED') },
                      { id: 'PARTIAL_REFUND', name: translate('ra.status.PARTIAL_REFUND') },
                      { id: 'EXPIRED', name: translate('ra.status.EXPIRED_MALE') },
                      { id: 'REINVOICED', name: translate('ra.status.REINVOICED') },
                      { id: 'VOID', name: translate('ra.status.VOID') },
                      { id: 'ERROR', name: translate('ra.status.ERROR') },
                      { id: 'UNDEFINED', name: translate('ra.status.UNDEFINED') },
                      { id: 'PENDING_RESERVATION', name: translate('ra.status.PENDING_RESERVATION') },
                      { id: 'INVOICE_IN_PROGRESS', name: translate('ra.status.INVOICE_IN_PROGRESS') },
                      { id: 'INVOICE_REQUESTED', name: translate('ra.status.INVOICE_REQUESTED') },
                      { id: 'CANCELLATION_IN_PROGRESS', name: translate('ra.status.CANCELLATION_IN_PROGRESS') },
                      { id: 'VOID_IN_PROGRESS', name: translate('ra.status.VOID_IN_PROGRESS') },
                      { id: 'FAILED_INVOICED', name: translate('ra.status.FAILED_INVOICED') },
                      { id: 'AFTER_SALES', name: translate('ra.status.AFTER_SALES') },
                      { id: 'PARTIAL_INVOICED', name: translate('ra.status.PARTIAL_INVOICED') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.item__payment_status && (
              <RemoveFilterField
                prop={'item__payment_status'}
                fields={['item__payment_status__in']}
                width={400}
              >
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.payment_status')}</InputLabel>
                  <SelectInput
                    source='item__payment_status__in'
                    options={[
                      { id: 'GUARANTEED', name: translate('ra.status.GUARANTEED') },
                      { id: 'UNPAID', name: translate('ra.status.UNPAID') },
                      { id: 'PAID', name: translate('ra.status.PAID') },
                      { id: 'PARTIALLY_PAID', name: translate('ra.status.PARTIALLY_PAID') },
                      { id: 'NOT_REQUIRED', name: translate('ra.status.NOT_REQUIRED') },
                      { id: 'IN_REVIEW', name: translate('ra.status.IN_REVIEW') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.pax_item__ticket_number__in && (
              <RemoveFilterField
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'pax_item__ticket_number__in'}
                fields={['pax_item__ticket_number__in']}
                width={300}
              >
                <TextInput
                  parse={(value) => value?.trim()}
                  label={translate('ra.field.item_ticket_number')}
                  fullWidth
                  source='pax_item__ticket_number__in'
                />
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.item_billing_status && (
              <RemoveFilterField prop={'item_billing_status'} fields={['item_billing_status']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.item_billing_status')}</InputLabel>
                  <SelectInput
                    source='item_billing_status'
                    options={[
                      { id: 'PENDING', name: translate('ra.status.PENDING_BILLING') },
                      { id: 'TO_INVOICE', name: translate('ra.status.TO_BILLING') },
                    ]}
                    optionsText={['name']}
                  />
                </FormControl>
              </RemoveFilterField>
            )} */}
            {displayedFilters?.supplier_id__in && (
              <RemoveFilterField prop={'supplier_id__in'} fields={['supplier_id__in']} width={300}>
                <SelectArrayInputFn
                  source='supplier_id__in'
                  queryName='supplier-all'
                  label={translate('ra.field.supplier')}
                  queryFn={() => supplierServices.getByParam({ limit: 999 })}
                  optionsText={['name']}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.promotion_code && (
              <RemoveFilterField prop={'promotion_code'} fields={['promotion_code']} width={300}>
                <PromotionFilterInput
                  source='promotion_code'
                  label={translate('ra.field.promotion')}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.provider_code__in && (
              <RemoveFilterField prop={'provider_code__in'} fields={['provider_code__in']} width={300}>
                <TextInput label={translate('ra.field.provider_code')} fullWidth source='provider_code__in' />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__autocancel_date && (
              <RemoveFilterField
                prop={'item__autocancel_date'}
                fields={['item__autocancel_date__gte', 'item__autocancel_date__lte']}
                width={500}
              >
                <DateInput
                  label={translate('ra.field.start_autocancel_date')}
                  fullWidth
                  source='item__autocancel_date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_autocancel_date')}
                  fullWidth
                  source='item__autocancel_date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__updated_at && (
              <RemoveFilterField
                prop={'item__updated_at'}
                fields={['item__updated_at__lte', 'item__updated_at__gte']}
                width={500}
              >
                <DateTimeInput
                  label={translate('ra.field.start_last_modification_date')}
                  fullWidth
                  source='item__updated_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_last_modification_date')}
                  fullWidth
                  source='item__updated_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.tag__id__in && (
              <RemoveFilterField prop={'tag__id__in'} fields={['tag__id__in']} width={500}>
                <SelectArrayInputFn
                  source='tag__id__in'
                  queryName='tag-all'
                  label={translate('ra.field.tags')}
                  queryFn={() => tagServices.getTagsWithParams({ limit: 999 })}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.sales_id__in && (
              <RemoveFilterField prop={'sales_id__in'} fields={['sales_id__in']} width={300}>
                <TextInput label={translate('ra.field.tma_business')} fullWidth source='sales_id__in' />
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_situation && (
              <RemoveFilterField
                prop={'billing_situation'}
                fields={['billing_situation', 'billing__situation__in', 'billing__situation__not_in']}
                width={300}
              >
                <BooleanInput
                  label={translate('ra.field.billing_situation')}
                  fullWidth
                  source='billing_situation'
                />
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.billing_automatic && (
              <RemoveFilterField prop={'billing_automatic'} fields={['billing_automatic']} width={300}>
                <BooleanInput
                  label={translate('ra.field.billing_automatic')}
                  fullWidth
                  source='billing_automatic'
                />
              </RemoveFilterField>
            )} */}
            {displayedFilters?.item__is_billable && (
              <RemoveFilterField prop={'item__is_billable'} fields={['item__is_billable']} width={300}>
                <BooleanInput
                  label={translate('ra.field.is_billable')}
                  fullWidth
                  source='item__is_billable'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_smartbook && (
              <RemoveFilterField prop={'item__has_smartbook'} fields={['item__has_smartbook']} width={200}>
                <BooleanInput
                  label={translate('ra.field.smartbookTitle')}
                  fullWidth
                  source='item__has_smartbook'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_flexible_rate && (
              <RemoveFilterField prop={'item__has_flexible_rate'} fields={['item__has_flexible_rate']} width={200}>
                <BooleanInput
                  label={translate('ra.field.flexi')}
                  fullWidth
                  source='item__has_flexible_rate'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_ancillary_hepstar && (
              <RemoveFilterField prop={'item__has_ancillary_hepstar'} fields={['item__has_ancillary_hepstar']} width={300}>
                <BooleanInput label={translate('ra.field.hepstar')} fullWidth source='item__has_ancillary_hepstar' />
              </RemoveFilterField>
            )}
            {displayedFilters?.item_ancillary__status && (
              <RemoveFilterField
                prop={'item_ancillary__status'}
                fields={['item_ancillary__status']}
                width={400}
              >
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.hepstar_status')}</InputLabel>
                  <SelectInput
                    source='item_ancillary__status'
                    options={[
                      { id: 'PENDING', name: translate('ra.status.PENDING_BILLING') },
                      { id: 'CONFIRMED', name: translate('ra.status.CONFIRMED') },
                      { id: 'CANCELLED', name: translate('ra.status.CANCELLED') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.item_ancillary__transaction_code__in && (
              <RemoveFilterField
                prop={'item_ancillary__transaction_code__in'}
                fields={['item_ancillary__transaction_code__in']}
                width={400}
              >
                <TextInput
                  label={translate('ra.field.hepstar_code')}
                  fullWidth
                  source='item_ancillary__transaction_code__in'
                  helperText={translate('ra.message.separate_by_comma')}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item_ancillary__product_name && (
              <RemoveFilterField
                prop={'item_ancillary__product_name'}
                fields={['item_ancillary__product_name']}
                width={450}
              >
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.hepstar_product')}</InputLabel>
                  <SelectInput
                    source='item_ancillary__product_name'
                    options={[
                      { id: 'Refundable booking for listed reasons', name: translate('ra.field.hepstar_product_refundable') },
                      { id: 'Voye SIM bundle', name: translate('ra.field.hepstar_product_esim') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.item__has_service_package && (
              <RemoveFilterField prop={'item__has_service_package'} fields={['item__has_service_package']} width={250}>
                <BooleanInput label={translate('ra.title.servicePackage')} fullWidth source='item__has_service_package' />
              </RemoveFilterField>
            )} */}
            {displayedFilters?.invoice_date && (
              <RemoveFilterField
                prop={'invoice_date'}
                fields={['invoice__invoice_date__gte', 'invoice__invoice_date__lte']}
                width={500}
              >
                <DateInput
                  defaultValue={toISODate()}
                  label={translate('ra.field.start_invoice_date')}
                  fullWidth
                  source='invoice__invoice_date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  defaultValue={toISODate()}
                  label={translate('ra.field.end_invoice_date')}
                  fullWidth
                  source='invoice__invoice_date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.bases_id__in && (
              <RemoveFilterField prop={'bases_id__in'} fields={['bases_id__in']} width={300}>
                <SelectArrayInputFn
                  source='bases_id__in'
                  queryName='base-all'
                  label={translate('ra.field.base_to_billing')}
                  queryFn={() => basesServices.getByAll()}
                  optionsText={['name']}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <Button variant='outlined' color='primary' type='submit'>
                    {translate('ra.action.filter')}
                  </Button>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
      </FormProvider>
      <FormProvider {...usersForm}>
        <Dialog open={openSellerDialog} maxWidth='md' fullWidth onClose={()=>setOpenSellerDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.seller')}
                <IconButton size='small' onClick={()=>setOpenSellerDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='seller'
                    options={usersList}
                    loading={usersLoading}
                    onChange={(val)=>{
                      form.setValue('user_id__in', val.target.value.map(user=>user['id_reservation_user']))
                      usersForm.setValue('seller', val.target.value)
                      localStorage.setItem('seller', JSON.stringify(val.target.value))
                    }}
                    multiple
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
            <Dialog open={openCommercialResponsibleDialog} maxWidth='md' fullWidth onClose={()=>setOpenCommercialResponsibleDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.commercial_responsible')}
                <IconButton size='small' onClick={()=>setOpenCommercialResponsibleDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='commercial_responsible'
                    optionValue='id'
                    options={usersList}
                    loading={usersLoading}
                    multiple
                    onChange={(val)=>{
                      form.setValue('business_unit__salesperson_user_id__in', val.target.value.map(user=>user['id_reservation_user']))
                      usersForm.setValue('commercial_responsible', val.target.value)
                      localStorage.setItem('commercial_responsible', JSON.stringify(val.target.value))
                    }}
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
            <Dialog open={openManagerResponsibleDialog} maxWidth='md' fullWidth onClose={()=>setOpenManagerResponsibleDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.manager_responsible')}
                <IconButton size='small' onClick={()=>setOpenManagerResponsibleDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='manager_responsible'
                    options={usersList}
                    loading={usersLoading}
                    multiple
                    onChange={(val)=>{
                      form.setValue('business_unit__user_id__in', val.target.value.map(user=>user['id_reservation_user']))
                      usersForm.setValue('manager_responsible', val.target.value)
                      localStorage.setItem('manager_responsible', JSON.stringify(val.target.value))
                    }}
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
      </FormProvider>
    </>
  )
}
